import React from 'react';
import { Block, VideoBlockContent } from '../types';
import { apiJourneyBrainAsk } from 'src/utils/journeyApi';

import showdown from 'showdown';
import { generateBlockId } from '../helpers/generate-block-id';
import { useEditorStore } from '../editor-store';
import { findSectionByBlockId } from '../helpers/section/find-section-by-block-id';
import { NeueJourneyButton } from '../components/neue-button/journey';
import { NeueListbox } from 'src/common/form/NeueListbox';
import { NeueJourneyLoadingSpinner } from '../components/loading/journey-spinner';
import { functionNoop } from 'src/utils/function/noop';

type Props = {
  block: Block;
};

type Option = {
  label: string;
  value: string;
};

const OUTPUT_STYLES: Option[] = [
  {
    label: 'Verbose',
    value: 'verbose',
  },
  {
    label: 'Concise',
    value: 'concise',
  },
  {
    label: 'Bullet points',
    value: 'bullet_points',
  },
];

const OUTPUT_TYPES: Option[] = [
  {
    label: 'Summary',
    value: 'summary',
  },
  {
    label: 'Sales Follow Up',
    value: 'sales_followup',
  },
];

export const EditVideoControlPanelContent = ({ block }: Props) => {
  const blockContent = block.content as VideoBlockContent;
  const dispatchUserEditorAction = useEditorStore((state) => state.dispatchUserEditorAction);
  const selectBlock = useEditorStore((state) => state.selectBlock);
  const setScrollToBlockId = useEditorStore((state) => state.setScrollToBlockId);
  const sections = useEditorStore((state) => state.layout.sections);

  const [generatingSummary, setGeneratingSummary] = React.useState(false);
  const [outputType, setOutputType] = React.useState<Option | null>(OUTPUT_TYPES[0]);
  const [outputStyle, setOutputStyle] = React.useState<Option | null>(OUTPUT_STYLES[0]);

  const onSummaryGenerated = (summary: string) => {
    // add a new text block with the summary
    const blockId = generateBlockId();
    const sectionId = findSectionByBlockId(sections, block.id)?.id;

    dispatchUserEditorAction({
      type: 'append-block',
      id: blockId,
      sectionId,
      content: {
        type: 'text',
        value: summary,
        horizontalAlignment: 'left',
      },
      layoutExtraParams: {
        width: '100%',
      },
      addToNewRow: true,
    });
    selectBlock(blockId);
    setScrollToBlockId(blockId);
  };

  const convertMarkdownToHTML = (markdown: string) => {
    const converter = new showdown.Converter();
    return converter.makeHtml(markdown);
  };

  const onGenerateSummary = () => {
    setGeneratingSummary(true);
    apiJourneyBrainAsk('summarize_content', {
      content_uuid: blockContent.contentUUID,
      output_type: outputType?.value || '',
      output_style: outputStyle?.value || '',
    }).then((r) => {
      const { response: summary } = r;
      setGeneratingSummary(false);
      if (summary) {
        const html = convertMarkdownToHTML(summary);
        onSummaryGenerated && onSummaryGenerated(html);
      }
    });
  };

  return (
    <div className='flex flex-col space-y-4'>
      <div className='text-bedrock-p-small'>Summarize your video or create a sales follow up</div>
      <div className='flex flex-col space-y-2'>
        <NeueListbox
          listBoxClassName='w-[216px]'
          options={OUTPUT_TYPES}
          renderValue={(option: Option) => option.label}
          renderKey={(option: Option) => option.value}
          selected={outputType}
          onChange={(option: Option) => {
            setOutputType(option);
          }}
        />
        <NeueListbox
          listBoxClassName='w-[216px]'
          options={OUTPUT_STYLES}
          renderValue={(option: Option) => option.label}
          renderKey={(option: Option) => option.value}
          selected={outputStyle}
          onChange={(option: Option) => {
            setOutputStyle(option);
          }}
        />
      </div>
      <NeueJourneyButton type='button' onClick={onGenerateSummary} className='!h-10' disabled={generatingSummary}>
        {generatingSummary ? <NeueJourneyLoadingSpinner size='16' /> : <span>Generate</span>}
      </NeueJourneyButton>
    </div>
  );
};
